/**
 * API mutator — strict adapter used by generated Orval clients and direct calls.
 *
 * Translates Orval request config into an httpClient call, validates the
 * canonical { data, meta } envelope, and propagates strongly-typed errors.
 * Non-JSON responses or malformed envelopes are rejected as contract violations.
 */
import type { ApiResponseMeta, ApiSuccessEnvelope } from '@finance/contracts';
import { httpClient, parseApiError, ApiError } from './http-client.js';

export { ApiError };

export interface MutatorRequestConfig {
  url: string;
  method: string;
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function buildUrl(baseUrl: string, params?: Record<string, unknown>): string {
  if (!params || Object.keys(params).length === 0) return baseUrl;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, String(item));
      }
    } else {
      searchParams.append(key, String(value));
    }
  }
  const query = searchParams.toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
}

function isApiResponseMeta(value: unknown): value is ApiResponseMeta {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).timestamp === 'string' &&
    typeof (value as Record<string, unknown>).requestId === 'string'
  );
}

function isApiSuccessEnvelope(value: unknown): value is ApiSuccessEnvelope<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    'meta' in value &&
    isApiResponseMeta((value as Record<string, unknown>).meta)
  );
}

async function readJsonEnvelope(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiError(
      'CONTRACT_VIOLATION',
      response.status,
      'errors.contract.nonJsonResponse',
      `Expected JSON response, received ${contentType || 'empty'}`,
    );
  }

  try {
    return await response.json();
  } catch {
    throw new ApiError(
      'CONTRACT_VIOLATION',
      response.status,
      'errors.contract.invalidJson',
      'Failed to parse response body as JSON',
    );
  }
}

/**
 * Fetch the authenticated profile through the generated authControllerMe
 * endpoint. Kept as a local helper because the session provider needs a
 * raw profile payload, not an Orval query hook.
 */
export async function fetchProfile(): Promise<unknown> {
  const res = await httpClient('/api/auth/me', { method: 'GET', skipRefresh: true });
  if (!res.ok) throw await parseApiError(res);
  const json = await readJsonEnvelope(res);
  if (!isApiSuccessEnvelope(json)) {
    throw new ApiError('CONTRACT_VIOLATION', res.status, 'errors.contract.missingEnvelope', 'Profile response missing canonical envelope');
  }
  return json.data;
}

export async function customMutator<T>(config: MutatorRequestConfig): Promise<T>;
export async function customMutator<T>(url: string, init?: RequestInit): Promise<T>;
export async function customMutator<T>(
  configOrUrl: MutatorRequestConfig | string,
  init?: RequestInit,
): Promise<T> {
  const config: MutatorRequestConfig =
    typeof configOrUrl === 'string'
      ? {
          url: configOrUrl,
          method: init?.method ?? 'GET',
          headers: (init?.headers as Record<string, string> | undefined) ?? undefined,
          signal: init?.signal ?? undefined,
          data: init?.body,
        }
      : configOrUrl;

  const url = buildUrl(config.url, config.params);
  const hasBody = config.data !== undefined;

  const response = await httpClient(url, {
    method: config.method,
    body: hasBody
      ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data))
      : undefined,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...config.headers,
    },
    signal: config.signal,
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  const json = await readJsonEnvelope(response);

  if (!isApiSuccessEnvelope(json)) {
    throw new ApiError(
      'CONTRACT_VIOLATION',
      response.status,
      'errors.contract.missingEnvelope',
      'Response missing canonical { data, meta } envelope',
    );
  }

  return json as T;
}
